/**
 * The CSV file preset of the importer (piece 31; decision 9280): one entity's
 * file a run, its column map, its caps, the header's fold, the sample file and
 * the answers the import screen reads. 🔒 The column maps are the whole field
 * set a CSV run writes, a decision and never a setting (decisions 0982, 2890).
 */
import { comparablePhone, comparablePostalCode, normaliseCompanyName, normaliseStreetLine, normaliseText } from '../records/normalise';
import { csvCell } from '../reports/reports';
import type { ImportBlockValue, ImportRowEntry, ImportRunStatusValue } from './importRun';
import { IMPORT_PATH } from './importRun';

export const CSV_IMPORT_ENTITIES = ['Companies', 'People', 'Locations', 'Items'] as const;
export type CsvImportEntity = (typeof CSV_IMPORT_ENTITIES)[number];

/** 🔒 The block each entity's run is (decision 9280). */
export const CSV_BLOCK_OF: Readonly<Record<CsvImportEntity, ImportBlockValue>> = { Companies: 'CsvCompanies', People: 'CsvPeople', Locations: 'CsvLocations', Items: 'CsvItems' };

export function csvEntityOfBlock(block: ImportBlockValue): CsvImportEntity | null {
  return CSV_IMPORT_ENTITIES.find((entity) => CSV_BLOCK_OF[entity] === block) ?? null;
}

/** 🔒 A file is at most 5 MB and 10,000 data rows (decision 9280); a larger one is refused whole, naming the limit. */
export const CSV_IMPORT_MAX_BYTES = 5 * 1024 * 1024;
export const CSV_IMPORT_MAX_ROWS = 10_000;

/** One column of an entity's map: the header a file carries, the field it writes, and whether the natural key needs it. */
export interface CsvColumn {
  header: string;
  field: string;
  /** `true` is required alone; a group name is required with the others of its group, any one of them enough. */
  required: true | string | false;
}

const col = (header: string, field: string, required: CsvColumn['required'] = false): CsvColumn => ({ header, field, required });

/** 🔒 Each entity's column map (decision 9280), in the order the sample file lists them. */
export const CSV_COLUMNS: Readonly<Record<CsvImportEntity, readonly CsvColumn[]>> = {
  Companies: [
    col('Name', 'displayName', true), col('EIN', 'ein', 'key'), col('Postal Code', 'postalCode', 'key'),
    col('Website', 'website'), col('Main Phone', 'mainPhoneNumber'), col('Customer', 'isCustomer'), col('Vendor', 'isVendor'), col('Manufacturer', 'isManufacturer'),
    col('Address Line 1', 'addressLine1'), col('Address Line 2', 'addressLine2'), col('City', 'locality'), col('State', 'administrativeArea'), col('Country', 'countryCode'),
  ],
  People: [
    col('First Name', 'firstName', true), col('Last Name', 'lastName', true), col('Work Email', 'workEmail', 'key'), col('Company', 'company', 'key'),
    col('Title', 'title'), col('Work Phone', 'workPhone'), col('Mobile Phone', 'mobilePhone'),
  ],
  Locations: [
    col('Address Line 1', 'addressLine1', true), col('Postal Code', 'postalCode', true), col('Country', 'countryCode', true),
    col('Address Line 2', 'addressLine2'), col('City', 'locality'), col('State', 'administrativeArea'),
  ],
  Items: [
    col('Manufacturer', 'manufacturer', true), col('Part Number', 'partNumber', true), col('Name', 'name', true),
    col('SKU', 'sku'), col('Category', 'category'), col('Unit', 'unit'), col('Sales Description', 'salesDescription'),
    col('List Price', 'listPrice'), col('Purchase Price', 'purchasePrice'), col('Sales Price', 'salesPrice'),
  ],
};

/** 🔒 A header is met by its folded name (decision 9280): case, spaces, underscores and hyphens ignored. */
export function foldCsvHeader(header: string): string {
  return header.trim().toLowerCase().replace(/[\s_-]+/g, '');
}

/** One header of the file as the map met it: the field it writes, or null where the map names none and it is ignored. */
export interface CsvColumnMet {
  header: string;
  field: string | null;
  /** The map's own header word for the field, which the screen shows beside the file's. */
  mapped: string | null;
}

/** The header met against an entity's map: each header's field, and the required columns it lacks, a group as one sentence. */
export function meetCsvHeader(entity: CsvImportEntity, header: readonly string[]): { columns: CsvColumnMet[]; missing: string[] } {
  const map = CSV_COLUMNS[entity];
  const taken = new Set<string>();
  const columns = header.map((name) => {
    const column = map.find((one) => foldCsvHeader(one.header) === foldCsvHeader(name) && !taken.has(one.field)) ?? null;
    if (column) taken.add(column.field);
    return { header: name, field: column?.field ?? null, mapped: column?.header ?? null };
  });
  const missing: string[] = [];
  for (const column of map) if (column.required === true && !taken.has(column.field)) missing.push(column.header);
  const groups = [...new Set(map.flatMap((column) => (typeof column.required === 'string' ? [column.required] : [])))];
  for (const group of groups) {
    const members = map.filter((column) => column.required === group);
    if (!members.some((column) => taken.has(column.field))) missing.push(members.map((column) => column.header).join(' or '));
  }
  return { columns, missing };
}

/** The required cells a row lacks, named by the map's headers; a group lacking every member names them joined by or. */
export function missingCsvCells(entity: CsvImportEntity, values: Readonly<Record<string, string>>): string[] {
  const map = CSV_COLUMNS[entity];
  const has = (field: string) => (values[field] ?? '').trim() !== '';
  const missing = map.filter((column) => column.required === true && !has(column.field)).map((column) => column.header);
  const groups = [...new Set(map.flatMap((column) => (typeof column.required === 'string' ? [column.required] : [])))];
  for (const group of groups) {
    const members = map.filter((column) => column.required === group);
    if (!members.some((column) => has(column.field))) missing.push(members.map((column) => column.header).join(' or '));
  }
  return missing;
}

/** The map's header word for a field, which a change line and a reason name. */
export function csvHeaderOf(entity: CsvImportEntity, field: string): string {
  return CSV_COLUMNS[entity].find((column) => column.field === field)?.header ?? field;
}

/** 🔒 The sample file (decision 9280): the entity's header alone, built here so it can never disagree with the map. */
export function csvSampleFile(entity: CsvImportEntity): string {
  return `${CSV_COLUMNS[entity].map((column) => csvCell(column.header)).join(',')}\n`;
}

export function csvSampleFilename(entity: CsvImportEntity): string {
  return `${entity.toLowerCase()}-sample.csv`;
}

/** A yes or no cell: yes, y, true, 1 or x is true; no, n, false or 0 is false; anything else is not a flag. */
export function csvFlag(text: string): boolean | null {
  const folded = text.trim().toLowerCase();
  if (['yes', 'y', 'true', '1', 'x'].includes(folded)) return true;
  if (['no', 'n', 'false', '0'].includes(folded)) return false;
  return null;
}

/** A money cell as dollars, a dollar sign and thousands commas allowed; null where it is not a number. */
export function csvMoney(text: string): number | null {
  const cleaned = text.replace(/[$,\s]/g, '');
  if (cleaned === '' || !/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100) / 100;
}

/** 🔒 A Location's natural key (piece 31): its folded address lines, its postal code and its country code. */
export function csvLocationKey(address: { addressLine1: string; addressLine2?: string | null; postalCode: string; countryCode: string }): string {
  return [normaliseStreetLine(address.addressLine1), normaliseStreetLine(address.addressLine2 ?? ''), comparablePostalCode(address.postalCode), address.countryCode.trim().toUpperCase()].join('|');
}

/** 🔒 A Company's name-and-postal key (piece 31): the folded name at the postal code. */
export function csvCompanyNameKey(name: string, postalCode: string): string {
  return `${normaliseCompanyName(name)}|${comparablePostalCode(postalCode)}`;
}

/** 🔒 A Person's name key (piece 31): the folded full name, at the company the Company cell folds to. */
export function csvPersonNameKey(firstName: string, lastName: string): string {
  return normaliseText(`${firstName} ${lastName}`);
}

/** Two phone cells name one line where their comparable digits agree. */
export function samePhone(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = comparablePhone(a ?? '');
  return left !== '' && left === comparablePhone(b ?? '');
}

/** One field a row would change on the record here: the field, its header, the value here and the value the file gives. */
export interface CsvChange {
  field: string;
  header: string;
  from: string | null;
  to: string;
}

/** 🔒 One entry of a CSV run's `rows` column (decision 9280): a run's entry as every block writes it, its kind CsvRow and its id the row's line. */
export type CsvRowEntry = Omit<ImportRowEntry, 'sourceKind'> & { sourceKind: 'CsvRow' };

export type CsvRowOutcome = 'Create' | 'Update' | 'Unchanged' | 'Reject';
/** What happened to a row at its write: Created, Updated or Unchanged, Skipped by a person, or Refused by the service. */
export type CsvRowWritten = 'Created' | 'Updated' | 'Unchanged' | 'Skipped' | 'Refused';

/** One staged row as the screen reads it. */
export interface CsvRowView {
  id: string;
  sequence: number;
  /** The row's line in the file, the header being line 1. */
  line: number;
  label: string;
  outcome: CsvRowOutcome;
  reason: string | null;
  /** The record here the row matched, with its page; null for a create or a rejected row. */
  target: { label: string; route: string | null } | null;
  changes: CsvChange[];
  /** The warnings the row's values met, written as a NeedsReview flag at the write and never refusing it. */
  notes: string[];
  decision: 'Accept' | 'Skip' | null;
  written: CsvRowWritten | null;
  refusal: string | null;
}

/** A CSV run as the screen reads it. */
export interface CsvRunView {
  id: string;
  entity: CsvImportEntity;
  status: ImportRunStatusValue;
  runBy: string;
  startedAt: string;
  finishedAt: string | null;
  columns: CsvColumnMet[];
  createdCount: number;
  updatedCount: number;
  unchangedCount: number;
  rejectedCount: number;
  diffFileId: string | null;
  /** 🔒 T3336: the sentence a run ended with, such as the diff file that was not written; null when nothing failed. */
  failureReason: string | null;
  /** The rows no person decided and no write reached yet. */
  undecided: number;
  /** 🔒 Write all is writing the run now; the run stays Staged until it ends. */
  writing: boolean;
  rows: CsvRowView[];
}

export type CsvRunSummary = Omit<CsvRunView, 'rows' | 'columns'>;

/** 🔒 The preset as the screen reads it: per entity, its waiting run, which holds the entity (decision 9280), and its last run. */
export interface CsvImportPresetView {
  entities: { entity: CsvImportEntity; staged: CsvRunSummary | null; last: CsvRunSummary | null }[];
}

export const CSV_IMPORT_PATH = `${IMPORT_PATH}/csv`;
export const CSV_IMPORT_RUNS_PATH = `${CSV_IMPORT_PATH}/runs`;

export function csvImportRunPath(runId: string): string {
  return `${CSV_IMPORT_RUNS_PATH}/${encodeURIComponent(runId)}`;
}

export function csvImportWriteAllPath(runId: string): string {
  return `${csvImportRunPath(runId)}/write-all`;
}

export function csvImportClosePath(runId: string): string {
  return `${csvImportRunPath(runId)}/close`;
}

export function csvImportRowPath(runId: string, rowId: string): string {
  return `${csvImportRunPath(runId)}/rows/${encodeURIComponent(rowId)}`;
}
